/**
 * LiFi Swap Quote Hook
 *
 * Fetches LiFi swap quotes for wallet-to-wallet swaps.
 */

import { useQuery } from '@tanstack/react-query'
import { Address } from 'viem'
import { fetchLifiQuote, lifiAddressOverride } from './fetchLifiQuote'

export interface LifiSwapQuoteParams {
  /** Token to swap from */
  tokenIn?: Address
  /** Token to swap to */
  tokenOut?: Address
  /** Amount to swap (in wei) */
  amount?: string
  /** User's wallet address */
  userAddress?: Address
  /** Slippage tolerance in basis points (50 = 0.5%) */
  slippage?: number
  /** Whether the hook is enabled */
  enabled?: boolean
  /** Token info for display */
  tokenInInfo?: {
    symbol: string
    name: string
    decimals: number
  }
  tokenOutInfo?: {
    symbol: string
    name: string
    decimals: number
  }
}

export interface LifiSwapQuoteResult {
  /** Approval address for token allowance (LiFi's estimate.approvalAddress) */
  approvalAddress: Address
  /** Expected output amount (wei) */
  outputAmount: string
  /** Minimum output amount after slippage (wei) */
  minOutputAmount: string
  /** Input amount (wei) */
  inputAmount: string
  /** Token in info */
  tokenIn: {
    address: string
    symbol: string
    name: string
    decimals: number
  }
  /** Token out info */
  tokenOut: {
    address: string
    symbol: string
    name: string
    decimals: number
  }
  /** Original amount in for display */
  amountIn: string
  /** Minimum output for display */
  amountOutMin: string
  /** Slippage used */
  slippage: number
  /** Transaction target address (for swap execution) */
  routerAddress: Address
  /** Transaction calldata (for swap execution) */
  calldata: `0x${string}`
  /** Native value to send with transaction (for native token swaps) */
  txValue: string
}

export interface UseLifiSwapQuoteResult {
  quote: LifiSwapQuoteResult | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export function useLifiSwapQuote({
  tokenIn,
  tokenOut,
  amount,
  userAddress,
  slippage = 50,
  enabled = true,
  tokenInInfo,
  tokenOutInfo,
}: LifiSwapQuoteParams): UseLifiSwapQuoteResult {
  const query = useQuery({
    queryKey: ['lifiSwapQuote', tokenIn, tokenOut, amount, userAddress, slippage],
    queryFn: async (): Promise<LifiSwapQuoteResult | null> => {
      if (!tokenIn || !tokenOut || !amount || !userAddress || !tokenInInfo || !tokenOutInfo) {
        return null
      }

      const lifiQuote = await fetchLifiQuote({
        fromToken: lifiAddressOverride(tokenIn),
        toToken: lifiAddressOverride(tokenOut),
        fromAmount: amount,
        fromAddress: userAddress,
        slippageBps: slippage,
      })

      return {
        approvalAddress: lifiQuote.estimate.approvalAddress as Address,
        outputAmount: lifiQuote.estimate.toAmount,
        minOutputAmount: lifiQuote.estimate.toAmountMin,
        inputAmount: lifiQuote.estimate.fromAmount,
        tokenIn: {
          address: tokenIn,
          symbol: tokenInInfo.symbol,
          name: tokenInInfo.name,
          decimals: tokenInInfo.decimals,
        },
        tokenOut: {
          address: tokenOut,
          symbol: tokenOutInfo.symbol,
          name: tokenOutInfo.name,
          decimals: tokenOutInfo.decimals,
        },
        amountIn: amount,
        amountOutMin: lifiQuote.estimate.toAmountMin,
        slippage,
        routerAddress: lifiQuote.transactionRequest.to,
        calldata: lifiQuote.transactionRequest.data,
        txValue: lifiQuote.transactionRequest.value ?? '0',
      }
    },
    enabled:
      enabled &&
      !!tokenIn &&
      !!tokenOut &&
      !!amount &&
      !!userAddress &&
      !!tokenInInfo &&
      !!tokenOutInfo &&
      tokenIn !== tokenOut &&
      BigInt(amount || '0') > 0n,
    staleTime: 10_000,
    refetchInterval: 12_000,
    gcTime: 30_000,
    retry: 2,
  })

  return {
    quote: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  }
}
